/**
 * Spoken names for the controls that show only a symbol.
 *
 * A button whose whole content is "‹" is, to TalkBack, a button with no name:
 * it announces "button" and stops. The three arrows that move through months,
 * weeks and years were exactly this — so a blind user could reach the calendar
 * and then not leave today. Same for the ✕ that clears a field and the bin that
 * deletes a label: the shape carries the meaning, and a shape is silent.
 *
 * Kept apart from i18n.js on purpose. These strings are never displayed; they
 * exist only to be read aloud, and mixing them into the visible translations
 * invites someone to "reuse" one on screen, where "Previous month" under an
 * arrow would look like a mistake.
 *
 * Resolution matches useT(): exact tag, then base language, then English.
 */
const LABELS = {
  en: { prevMonth: 'Previous month', nextMonth: 'Next month',
        prevWeek: 'Previous week',   nextWeek: 'Next week',
        prevYear: 'Previous year',   nextYear: 'Next year',
        clearEnd: 'Remove end time', deleteLabel: 'Delete label',
        renameSave: 'Save name',     renameCancel: 'Cancel renaming',
        close: 'Close', back: 'Back',              priority: 'Priority',
        include: 'Include this event', themeColor: 'Theme colour', color: 'Colour' },
  it: { prevMonth: 'Mese precedente', nextMonth: 'Mese successivo',
        prevWeek: 'Settimana precedente', nextWeek: 'Settimana successiva',
        prevYear: 'Anno precedente', nextYear: 'Anno successivo',
        clearEnd: "Togli l'ora di fine", deleteLabel: 'Elimina etichetta',
        renameSave: 'Salva il nome', renameCancel: 'Annulla la rinomina',
        close: 'Chiudi', back: 'Indietro',             priority: 'Priorità',
        include: 'Includi questo evento', themeColor: 'Colore del tema', color: 'Colore' },
  es: { prevMonth: 'Mes anterior', nextMonth: 'Mes siguiente',
        prevWeek: 'Semana anterior', nextWeek: 'Semana siguiente',
        prevYear: 'Año anterior', nextYear: 'Año siguiente',
        clearEnd: 'Quitar la hora de fin', deleteLabel: 'Eliminar etiqueta',
        renameSave: 'Guardar el nombre', renameCancel: 'Cancelar el cambio de nombre',
        close: 'Cerrar', back: 'Atrás', priority: 'Prioridad',
        include: 'Incluir este evento', themeColor: 'Color del tema', color: 'Color' },
  de: { prevMonth: 'Vorheriger Monat', nextMonth: 'Nächster Monat',
        prevWeek: 'Vorherige Woche', nextWeek: 'Nächste Woche',
        prevYear: 'Vorheriges Jahr', nextYear: 'Nächstes Jahr',
        clearEnd: 'Endzeit entfernen', deleteLabel: 'Etikett löschen',
        renameSave: 'Namen speichern', renameCancel: 'Umbenennen abbrechen',
        close: 'Schließen', back: 'Zurück', priority: 'Priorität',
        include: 'Diesen Termin einschließen', themeColor: 'Themenfarbe', color: 'Farbe' },
  fr: { prevMonth: 'Mois précédent', nextMonth: 'Mois suivant',
        prevWeek: 'Semaine précédente', nextWeek: 'Semaine suivante',
        prevYear: 'Année précédente', nextYear: 'Année suivante',
        clearEnd: "Supprimer l'heure de fin", deleteLabel: "Supprimer l'étiquette",
        renameSave: 'Enregistrer le nom', renameCancel: 'Annuler le renommage',
        close: 'Fermer', back: 'Retour', priority: 'Priorité',
        include: 'Inclure cet événement', themeColor: 'Couleur du thème', color: 'Couleur' },
  nl: { prevMonth: 'Vorige maand', nextMonth: 'Volgende maand',
        prevWeek: 'Vorige week', nextWeek: 'Volgende week',
        prevYear: 'Vorig jaar', nextYear: 'Volgend jaar',
        clearEnd: 'Eindtijd verwijderen', deleteLabel: 'Label verwijderen',
        renameSave: 'Naam opslaan', renameCancel: 'Hernoemen annuleren',
        close: 'Sluiten', back: 'Terug', priority: 'Prioriteit',
        include: 'Deze afspraak opnemen', themeColor: 'Themakleur', color: 'Kleur' },
  pt: { prevMonth: 'Mês anterior', nextMonth: 'Mês seguinte',
        prevWeek: 'Semana anterior', nextWeek: 'Semana seguinte',
        prevYear: 'Ano anterior', nextYear: 'Ano seguinte',
        clearEnd: 'Remover a hora de fim', deleteLabel: 'Eliminar etiqueta',
        renameSave: 'Guardar o nome', renameCancel: 'Cancelar a mudança de nome',
        close: 'Fechar', back: 'Voltar', priority: 'Prioridade',
        include: 'Incluir este evento', themeColor: 'Cor do tema', color: 'Cor' },
  'pt-BR': { prevMonth: 'Mês anterior', nextMonth: 'Próximo mês',
        prevWeek: 'Semana anterior', nextWeek: 'Próxima semana',
        prevYear: 'Ano anterior', nextYear: 'Próximo ano',
        clearEnd: 'Remover o horário de término', deleteLabel: 'Excluir etiqueta',
        renameSave: 'Salvar o nome', renameCancel: 'Cancelar a renomeação',
        close: 'Fechar', back: 'Voltar', priority: 'Prioridade',
        include: 'Incluir este evento', themeColor: 'Cor do tema', color: 'Cor' },
  ru: { prevMonth: 'Предыдущий месяц', nextMonth: 'Следующий месяц',
        prevWeek: 'Предыдущая неделя', nextWeek: 'Следующая неделя',
        prevYear: 'Предыдущий год', nextYear: 'Следующий год',
        clearEnd: 'Убрать время окончания', deleteLabel: 'Удалить метку',
        renameSave: 'Сохранить имя', renameCancel: 'Отменить переименование',
        close: 'Закрыть', back: 'Назад', priority: 'Приоритет',
        include: 'Включить это событие', themeColor: 'Цвет темы', color: 'Цвет' },
  pl: { prevMonth: 'Poprzedni miesiąc', nextMonth: 'Następny miesiąc',
        prevWeek: 'Poprzedni tydzień', nextWeek: 'Następny tydzień',
        prevYear: 'Poprzedni rok', nextYear: 'Następny rok',
        clearEnd: 'Usuń godzinę zakończenia', deleteLabel: 'Usuń etykietę',
        renameSave: 'Zapisz nazwę', renameCancel: 'Anuluj zmianę nazwy',
        close: 'Zamknij', back: 'Wstecz', priority: 'Priorytet',
        include: 'Uwzględnij to wydarzenie', themeColor: 'Kolor motywu', color: 'Kolor' },
  ro: { prevMonth: 'Luna anterioară', nextMonth: 'Luna următoare',
        prevWeek: 'Săptămâna anterioară', nextWeek: 'Săptămâna următoare',
        prevYear: 'Anul anterior', nextYear: 'Anul următor',
        clearEnd: 'Elimină ora de sfârșit', deleteLabel: 'Șterge eticheta',
        renameSave: 'Salvează numele', renameCancel: 'Anulează redenumirea',
        close: 'Închide', back: 'Înapoi', priority: 'Prioritate',
        include: 'Include acest eveniment', themeColor: 'Culoarea temei', color: 'Culoare' },
  zh: { prevMonth: '上个月', nextMonth: '下个月',
        prevWeek: '上一周', nextWeek: '下一周',
        prevYear: '上一年', nextYear: '下一年',
        clearEnd: '清除结束时间', deleteLabel: '删除标签',
        renameSave: '保存名称', renameCancel: '取消重命名',
        close: '关闭', back: '返回', priority: '优先级',
        include: '包含此事件', themeColor: '主题颜色', color: '颜色' },
  ja: { prevMonth: '前の月', nextMonth: '次の月',
        prevWeek: '前の週', nextWeek: '次の週',
        prevYear: '前の年', nextYear: '次の年',
        clearEnd: '終了時刻を削除', deleteLabel: 'ラベルを削除',
        renameSave: '名前を保存', renameCancel: '名前の変更を取り消す',
        close: '閉じる', back: '戻る', priority: '優先度',
        include: 'この予定を含める', themeColor: 'テーマの色', color: '色' },
  hi: { prevMonth: 'पिछला महीना', nextMonth: 'अगला महीना',
        prevWeek: 'पिछला सप्ताह', nextWeek: 'अगला सप्ताह',
        prevYear: 'पिछला वर्ष', nextYear: 'अगला वर्ष',
        clearEnd: 'समाप्ति समय हटाएँ', deleteLabel: 'लेबल हटाएँ',
        renameSave: 'नाम सहेजें', renameCancel: 'नाम बदलना रद्द करें',
        close: 'बंद करें', back: 'वापस', priority: 'प्राथमिकता',
        include: 'इस कार्यक्रम को शामिल करें', themeColor: 'थीम का रंग', color: 'रंग' },
  ko: { prevMonth: '이전 달', nextMonth: '다음 달',
        prevWeek: '이전 주', nextWeek: '다음 주',
        prevYear: '이전 해', nextYear: '다음 해',
        clearEnd: '종료 시간 지우기', deleteLabel: '라벨 삭제',
        renameSave: '이름 저장', renameCancel: '이름 변경 취소',
        close: '닫기', back: '뒤로', priority: '우선순위',
        include: '이 일정 포함', themeColor: '테마 색상', color: '색상' },
  ar: { prevMonth: 'الشهر السابق', nextMonth: 'الشهر التالي',
        prevWeek: 'الأسبوع السابق', nextWeek: 'الأسبوع التالي',
        prevYear: 'السنة السابقة', nextYear: 'السنة التالية',
        clearEnd: 'إزالة وقت الانتهاء', deleteLabel: 'حذف التصنيف',
        renameSave: 'حفظ الاسم', renameCancel: 'إلغاء إعادة التسمية',
        close: 'إغلاق', back: 'رجوع', priority: 'الأولوية',
        include: 'تضمين هذا الحدث', themeColor: 'لون السمة', color: 'لون' },
};

/** All the spoken labels for one language. Never returns undefined. */
export function a11y(language) {
  const tag = language || 'it';
  return LABELS[tag] || LABELS[tag.split('-')[0]] || LABELS.en;
}

export default a11y;
